import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Building,
  Clock,
  CalendarCheck,
  Plane,
  TimerReset,
  Calendar,
  Sparkles,
  CheckSquare,
  Shield,
  GraduationCap,
  LayoutList,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Cell,
} from "recharts";
import { startHrmDashboardTour } from "./dashboardTour";
import HrmDashboardBranchPicker, {
  buildHrmDashboardBranchSelectOptions,
} from "./shared/hrmDashboardBranchSelect";
import OnLeaveDetailModal from "./onLeave/OnLeaveDetailModal";
import BusinessTripDetailModal from "./businessTrip/BusinessTripDetailModal";

/** Khớp backend `dashboardScopeHelper` — role trong JWT/login thường là "Administrator", "Super Admin", … */
const SESSION_SUPER_ROLES = ["super admin", "superadmin", "administrator", "system administrator"];

function normalizeRoleLabel(r) {
  return String(r || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

function sessionHasDashboardBranchPicker() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    const roles = Array.isArray(u.roles) ? u.roles : [];
    return roles.some((r) => SESSION_SUPER_ROLES.includes(normalizeRoleLabel(r)));
  } catch {
    return false;
  }
}

/** Đích điều hướng khi click cả thẻ KPI (thẻ có 2 link: mặc định tới mục đầu, ví dụ nghỉ phép). */
function kpiCardNavigatePath(item) {
  if (item?.detailHref) return item.detailHref;
  if (Array.isArray(item?.detailLinks) && item.detailLinks[0]?.to) return item.detailLinks[0].to;
  return null;
}

/** Chuẩn hóa row dashboard công tác → props `BusinessTripDetailModal` (đồng bộ tên ngày / lý do từ chối). */
function businessTripRowForModal(row) {
  if (!row) return null;
  return {
    ...row,
    start_date: row.start_date ?? row.from_date,
    end_date: row.end_date ?? row.to_date,
    reject_reason: row.reject_reason ?? row.approval_note,
    location:
      row.location != null && String(row.location).trim() !== "" ? row.location : "—",
  };
}

export default function HRMDashboard() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedBranch, setSelectedBranch] = useState("all");

  const [branchOptions, setBranchOptions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLeaveRow, setDetailLeaveRow] = useState(null);
  const [detailTripRow, setDetailTripRow] = useState(null);
  const navigate = useNavigate();

  const openRecentLeaveBusinessDetail = (row) => {
    if (row?.type === "Nghỉ phép") {
      setDetailTripRow(null);
      setDetailLeaveRow(row);
    } else if (row?.type === "Công tác") {
      setDetailLeaveRow(null);
      setDetailTripRow(businessTripRowForModal(row));
    }
  };

  const canPickBranch = dashboard?.meta?.canPickBranch === true;
  const scopeLabel = dashboard?.meta?.scopeLabel || "";
  const sessionBranchPicker = useMemo(() => sessionHasDashboardBranchPicker(), []);
  const allowBranchPicker = canPickBranch || sessionBranchPicker;
  const showBranchFilter = !loading && allowBranchPicker;
  const showAdminKpis = dashboard?.meta?.isSuperAdmin === true;

  const branchSelectOptions = useMemo(
    () => buildHrmDashboardBranchSelectOptions(branchOptions),
    [branchOptions]
  );

  const monthPickerValue = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;

  const handleMonthPickerChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    const [y, m] = v.split("-").map(Number);
    if (Number.isFinite(y) && Number.isFinite(m)) {
      setSelectedYear(y);
      setSelectedMonth(m);
    }
  };

  const formatDateShort = (v) => {
    if (!v) return "—";
    const s = String(v);
    if (s.includes("T")) {
      const d = s.slice(0, 10);
      const [y, m, day] = d.split("-");
      if (y && m && day) return `${day}/${m}`;
    }
    if (s.includes("-")) {
      const [y, m, day] = s.split("-");
      if (y && m && day) return `${day}/${m}`;
    }
    if (s.includes("/")) return s;
    return s;
  };

  /** Số ngày trong tháng chọn (API đã cắt theo tháng); 1 chữ số thập phân (vd. 0,5 ngày nghỉ). */
  const formatDayCountOneDecimal = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = typeof v === "string" ? Number(v.replace(",", ".")) : Number(v);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("vi-VN", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };

  const dailyAttendance = dashboard?.charts?.dailyAttendance || [];
  const dailyOvertime = dashboard?.charts?.dailyOvertime || [];

  const attendanceAvgPeople = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const rows =
      y === selectedYear && m === selectedMonth
        ? dailyAttendance.filter((r) => {
            const d = new Date(`${r.dateKey}T12:00:00`);
            return d <= today;
          })
        : dailyAttendance;
    /** Trung bình chỉ trên ngày có chấm công (có phiếu / có người), không chia hết ngày trong tháng. */
    const workingDays = rows.filter((r) => {
      const people = Number(r.totalPeople) || 0;
      const records = Number(r.totalRecords) || 0;
      const hours = Number(r.workHours) || 0;
      return people > 0 || records > 0 || hours > 0;
    });
    if (!workingDays.length) return 0;
    const sum = workingDays.reduce((acc, r) => acc + (Number(r.totalPeople) || 0), 0);
    return Math.round((sum / workingDays.length) * 10) / 10;
  }, [dailyAttendance, selectedYear, selectedMonth]);

  const overtimeAvgHours = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const rows =
      y === selectedYear && m === selectedMonth
        ? dailyOvertime.filter((r) => {
            const d = new Date(`${r.dateKey}T12:00:00`);
            return d <= today;
          })
        : dailyOvertime;
    /** Trung bình chỉ trên ngày có giờ tăng ca (phút hoặc giờ > 0). */
    const workingDays = rows.filter((r) => {
      const hrs = Number(r.otHours) || 0;
      const mins = Number(r.otMinutes) || 0;
      return hrs > 0 || mins > 0;
    });
    if (!workingDays.length) return 0;
    const sum = workingDays.reduce((acc, r) => acc + (Number(r.otHours) || 0), 0);
    return Math.round((sum / workingDays.length) * 100) / 100;
  }, [dailyOvertime, selectedYear, selectedMonth]);

  const statsCards = useMemo(() => {
    const s = dashboard?.stats;
    if (!s) return [];
    const t = s.tasks || {};

    const adminRow = [
      {
        key: "branches",
        label: "Chi nhánh",
        sub: "Hoạt động / tổng",
        value: `${s.branches?.active ?? 0} / ${s.branches?.total ?? 0}`,
        icon: Building,
        color: "bg-rose-50",
        text: "text-rose-600",
        detailHref: "/hrm/branches",
      },
      {
        key: "employees",
        label: "Nhân viên",
        sub: "Hoạt động / tổng",
        value: `${s.employees?.active ?? 0} / ${s.employees?.total ?? 0}`,
        icon: Users,
        color: "bg-sky-50",
        text: "text-sky-600",
        detailHref: "/hrm/employees",
      },
      {
        key: "teachers",
        label: "Giáo viên (thỉnh giảng)",
        sub: "Hoạt động / tổng",
        value: `${s.teachersVisiting?.active ?? 0} / ${s.teachersVisiting?.total ?? 0}`,
        icon: GraduationCap,
        color: "bg-violet-50",
        text: "text-violet-600",
        detailHref: "/hrm/employees",
      },
      {
        key: "plans",
        label: "Kế hoạch",
        sub: "Tổng số / tháng",
        value: s.plansMonth ?? 0,
        icon: LayoutList,
        color: "bg-cyan-50",
        text: "text-cyan-700",
        detailHref: "/hrm/plans",
      },
    ];

    const peopleSuffix = (n) => ` (${Number(n) || 0} 人)`;
    /**
     * Super Admin không clamp. Còn lại (self, bộ phận, chi nhánh, nhóm…): có volume mà đếm 0 → tối thiểu 1 人.
     */
    const isSuperOnDashboard = dashboard?.meta?.isSuperAdmin === true;
    const effPeople = (raw, hasVolume) => {
      const n = Number(raw) || 0;
      if (!hasVolume || n > 0 || isSuperOnDashboard) return n;
      return 1;
    };

    const commonRow = [
      {
        key: "tasks",
        label: "Công việc",
        sub: "Tổng số / tháng",
        value: `${t.total ?? 0}${peopleSuffix(effPeople(t.peopleMonth, (t.total ?? 0) > 0))}`,
        icon: CheckSquare,
        color: "bg-amber-50",
        text: "text-amber-700",
        detailHref: "/hrm/tasks",
      },
      {
        key: "attendance-hours",
        label: "Chấm công",
        sub: "Tổng giờ làm / tháng",
        value:
          s.attendanceHoursMonth == null
            ? "—"
            : `${Number(s.attendanceHoursMonth).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}${peopleSuffix(
                effPeople(s.attendancePeopleMonth, Number(s.attendanceHoursMonth) > 0)
              )}`,
        icon: Clock,
        color: "bg-emerald-50",
        text: "text-emerald-600",
        detailHref: "/hrm/hrm_attendance",
      },
      {
        key: "overtime-hours",
        label: "Tăng ca",
        sub: "Tổng giờ / tháng",
        value:
          s.overtimeHoursMonth == null
            ? "—"
            : `${Number(s.overtimeHoursMonth).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}${peopleSuffix(
                effPeople(s.overtimePeopleMonth, Number(s.overtimeHoursMonth) > 0)
              )}`,
        icon: TimerReset,
        color: "bg-indigo-50",
        text: "text-indigo-600",
        detailHref: "/hrm/overtime",
      },
      {
        key: "leave-trip-days",
        label: "Nghỉ phép & công tác",
        sub: "Tổng ngày / tháng",
        value: `${s.leaveTripDaysMonth ?? 0}${peopleSuffix(
          effPeople(s.leaveTripPeopleMonth, Number(s.leaveTripDaysMonth ?? 0) > 0)
        )}`,
        icon: CalendarCheck,
        color: "bg-pink-50",
        text: "text-pink-600",
        detailLinks: [
          { to: "/hrm/on_leave", label: "Nghỉ phép" },
          { to: "/hrm/business_trip", label: "Công tác" },
        ],
      },
    ];

    return [...(showAdminKpis ? adminRow : []), ...commonRow];
  }, [dashboard, showAdminKpis]);

  useEffect(() => {
    if (!allowBranchPicker) return;
    let isMounted = true;
    (async () => {
      try {
        const { getAllBranches_Array } = await import("../../api/hrm/branches/info");
        const list = await getAllBranches_Array();
        if (!isMounted) return;
        setBranchOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load branches for HRM dashboard:", e);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [allowBranchPicker]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    (async () => {
      try {
        const { getHrmDashboard } = await import("../../api/hrm/dashboard");
        const branch_id = selectedBranch === "all" ? "all" : selectedBranch;
        const data = await getHrmDashboard({
          year: selectedYear,
          month: selectedMonth,
          branch_id,
        });
        if (!isMounted) return;
        setDashboard(data);
      } catch (e) {
        console.error("Failed to load HRM dashboard:", e);
        if (!isMounted) return;
        setDashboard(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [selectedYear, selectedMonth, selectedBranch]);

  const todayKey = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }, []);

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen space-y-8 pb-20">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6" data-tour="hrm-dashboard-header">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => window.setTimeout(() => startHrmDashboardTour(), 80)}
            title="Hướng dẫn nhanh"
            aria-label="Hướng dẫn nhanh"
            className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:bg-red-50 hover:border-red-300 shrink-0 mt-1"
          >
            <Sparkles className="h-5 w-5" strokeWidth={2.2} />
            <span className="pointer-events-none absolute bottom-full left-0 mb-2 rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 whitespace-nowrap opacity-0 translate-y-1 shadow-md transition-all group-hover:opacity-100 group-hover:translate-y-0">
              Hướng dẫn nhanh
            </span>
          </button>
          <div>
            <h1 className="text-3xl font-black text-gray-800 uppercase tracking-tight">Quản trị nhân sự</h1>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 italic">
              Dashboard theo phạm vi dữ liệu (data scope)
            </p>
            {!loading && scopeLabel ? (
              <div className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50/80 px-3 py-2 text-[11px] font-black uppercase tracking-wide text-red-800">
                <Shield className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                {scopeLabel}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="flex flex-wrap items-center justify-end gap-3 lg:max-w-[min(100%,28rem)] lg:flex-nowrap"
          data-tour="hrm-dashboard-filters"
        >
          {showBranchFilter ? (
            <HrmDashboardBranchPicker
              instanceId="hrm-dashboard-branch"
              className="flex-1 lg:min-w-[17rem] lg:flex-initial"
              options={branchSelectOptions}
              value={selectedBranch}
              onChange={setSelectedBranch}
              aria-label="Chọn chi nhánh dashboard HRM"
            />
          ) : null}

          <div className="relative min-w-0 shrink-0">
            <div className="absolute inset-y-0 left-4 z-10 flex items-center pointer-events-none text-red-600">
              <Calendar size={16} strokeWidth={3} />
            </div>
            <input
              type="month"
              value={monthPickerValue}
              onChange={handleMonthPickerChange}
              aria-label="Chọn tháng xem dashboard"
              title="Chọn tháng"
              className="h-[50px] w-full min-w-[11rem] rounded-2xl border border-gray-100 bg-white py-3 pl-11 pr-3 text-sm font-black uppercase tracking-wide text-gray-700 shadow-sm outline-none transition-all focus:border-red-200 focus:ring-2 focus:ring-red-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
            />
          </div>
        </div>
      </div>

      <div
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        data-tour="hrm-dashboard-kpis"
      >
        {loading ? (
          <div className="col-span-full text-center text-gray-500 py-10">Đang tải dashboard...</div>
        ) : (
          statsCards.map((item, index) => {
            const goTo = kpiCardNavigatePath(item);
            const openKpi = () => {
              if (goTo) navigate(goTo);
            };
            const onKeyCard = (e) => {
              if (!goTo) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(goTo);
              }
            };
            return (
              <motion.div
                key={item.key}
                role={goTo ? "link" : undefined}
                tabIndex={goTo ? 0 : undefined}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={goTo ? openKpi : undefined}
                onKeyDown={goTo ? onKeyCard : undefined}
                aria-label={goTo ? `Mở ${item.label}` : undefined}
                title={goTo ? (item.key === "leave-trip-days" ? "Click: danh sách nghỉ phép (công tác: menu HRM)" : undefined) : undefined}
                className={`bg-white rounded-[2rem] p-6 shadow-sm border border-gray-50 transition-all group relative overflow-hidden flex flex-col ${
                  goTo
                    ? "cursor-pointer hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-2"
                    : ""
                }`}
              >
                {goTo ? (
                  <span
                    className="pointer-events-none absolute top-3 right-4 z-20 text-[8px] font-black uppercase tracking-[0.2em] text-red-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                    aria-hidden
                  >
                    Chi tiết
                  </span>
                ) : null}
                <div className="flex justify-between items-start relative z-10">
                  <div className={`p-3 rounded-2xl ${item.color} ${item.text} group-hover:scale-110 transition-transform`}>
                    <item.icon size={24} />
                  </div>
                </div>
                <div className="mt-4 relative z-10 flex-1">
                  <div className="text-3xl font-black text-gray-800 tracking-tight">{item.value}</div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1 leading-snug">
                    {item.label}
                  </div>
                  <div className="text-[9px] font-semibold text-gray-300 uppercase tracking-wider mt-0.5">{item.sub}</div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      <div className="flex flex-col gap-8" data-tour="hrm-dashboard-charts">
        <div className="bg-white w-full rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight mb-2 flex items-center gap-2">
            <Clock className="text-blue-600" size={20} /> Chấm công theo ngày
          </h2>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">
            Số người có phiếu chấm công trong tháng đã chọn (cùng logic màn Chấm công)
          </p>
          <div className="h-[320px] w-full">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
                Đang tải biểu đồ...
              </div>
            ) : dailyAttendance.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
                Không có dữ liệu chấm công trong tháng.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyAttendance} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="dayLabel"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={48}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                  />
                  <Tooltip
                    formatter={(value, _n, ctx) => {
                      const row = ctx?.payload;
                      if (!row) return [value, "Người"];
                      return [`${row.totalPeople} 人 · ${row.totalRecords} phiếu`, "Ngày"];
                    }}
                    labelFormatter={(label) => `Ngày ${label}`}
                    cursor={{ fill: "#eff6ff" }}
                  />
                  <Bar dataKey="totalPeople" name="Người" fill="#2563eb" radius={[6, 6, 0, 0]} maxBarSize={22}>
                    {dailyAttendance.map((entry) => (
                      <Cell
                        key={entry.dateKey}
                        fill={entry.dateKey === todayKey ? "#ef4444" : "#2563eb"}
                      />
                    ))}
                  </Bar>
                  <ReferenceLine
                    isFront
                    y={attendanceAvgPeople}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="6 6"
                    label={{
                      value: `TB (ngày có CC): ${attendanceAvgPeople}`,
                      position: "insideTopRight",
                      fill: "#b45309",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white w-full rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
          <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight mb-2 flex items-center gap-2">
            <TimerReset className="text-indigo-600" size={20} /> Tăng ca theo ngày
          </h2>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6">
            Tổng giờ tăng ca theo ngày trong tháng (cùng logic màn Tăng ca)
          </p>
          <div className="h-[320px] w-full">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
                Đang tải biểu đồ...
              </div>
            ) : dailyOvertime.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm font-semibold text-gray-400">
                Không có dữ liệu tăng ca trong tháng.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyOvertime} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="dayLabel"
                    tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={48}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    formatter={(value, _n, ctx) => {
                      const row = ctx?.payload;
                      if (!row) return [value, "Giờ"];
                      return [`${row.otHours} giờ (${row.otMinutes} phút)`, "Tăng ca"];
                    }}
                    labelFormatter={(label) => `Ngày ${label}`}
                    cursor={{ fill: "#eef2ff" }}
                  />
                  <Bar dataKey="otHours" name="Giờ TC" fill="#4f46e5" radius={[6, 6, 0, 0]} maxBarSize={22}>
                    {dailyOvertime.map((entry) => (
                      <Cell
                        key={entry.dateKey}
                        fill={entry.dateKey === todayKey ? "#f97316" : "#4f46e5"}
                      />
                    ))}
                  </Bar>
                  <ReferenceLine
                    isFront
                    y={overtimeAvgHours}
                    stroke="#f59e0b"
                    strokeWidth={2}
                    strokeDasharray="6 6"
                    label={{
                      value: `TB (ngày có TC): ${overtimeAvgHours}`,
                      position: "insideTopRight",
                      fill: "#b45309",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100" data-tour="hrm-dashboard-recent">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight flex items-center gap-2">
            <Plane className="text-sky-600" size={20} />
            Nghỉ phép &amp; công tác
          </h2>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Tháng đã chọn</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] table-fixed border-collapse text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="w-[30%] px-4 pb-4 align-bottom text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Nhân viên
                </th>
                <th className="w-[13%] px-3 pb-4 align-bottom text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Loại đơn
                </th>
                <th className="w-[21%] px-3 pb-4 align-bottom text-[10px] font-black uppercase tracking-widest text-gray-400 text-right">
                  Thời gian
                </th>
                <th className="w-[11%] px-4 pb-4 align-bottom text-[10px] font-black uppercase tracking-widest text-gray-400 text-right whitespace-normal">
                  Số ngày
                </th>
                <th className="w-[25%] min-w-0 px-4 pb-4 align-bottom text-[10px] font-black uppercase tracking-widest text-gray-400 whitespace-normal">
                  Trạng thái
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(dashboard?.recentLeaveBusiness || []).map((row) => {
                const isLeave = row.type === "Nghỉ phép";
                return (
                  <tr
                    key={`${row.type}-${row.id}`}
                    role="button"
                    tabIndex={0}
                    title="Xem chi tiết"
                    className="group cursor-pointer hover:bg-gray-50/50 transition-colors align-top focus-visible:bg-sky-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sky-300/70"
                    onClick={() => openRecentLeaveBusinessDetail(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openRecentLeaveBusinessDetail(row);
                      }
                    }}
                  >
                    <td className="px-4 py-5 font-black text-gray-800 text-sm">
                      <div className="leading-tight break-words">{row.employee_name}</div>
                      {row.branch_name ? (
                        <div className="text-[10px] font-bold text-gray-400 mt-1">{row.branch_name}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-5 align-top">
                      <span
                        className={`inline-block px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                          isLeave ? "text-red-600 bg-red-50" : "text-blue-600 bg-blue-50"
                        }`}
                      >
                        {row.type}
                      </span>
                    </td>
                    <td className="px-3 py-5 align-top text-right font-bold text-gray-500 text-xs whitespace-nowrap">
                      {formatDateShort(row.from_date)} — {formatDateShort(row.to_date)}
                    </td>
                    <td className="px-4 py-5 align-top text-right tabular-nums">
                      <span className="inline-block min-w-[3.25rem] rounded-lg bg-gray-50 px-3 py-1.5 text-sm font-black text-gray-800">
                        {formatDayCountOneDecimal(row.day_count)}
                      </span>
                    </td>
                    <td className="px-4 py-5 align-top text-sm font-semibold leading-snug text-gray-700 border-l border-gray-100">
                      {row.status || "—"}
                    </td>
                  </tr>
                );
              })}
              {(dashboard?.recentLeaveBusiness || []).length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="py-10 text-center text-gray-400 text-sm">
                    Không có dữ liệu cho bộ lọc hiện tại.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {detailLeaveRow && (
        <OnLeaveDetailModal leave={detailLeaveRow} onClose={() => setDetailLeaveRow(null)} />
      )}
      <BusinessTripDetailModal
        isOpen={!!detailTripRow}
        onClose={() => setDetailTripRow(null)}
        data={detailTripRow}
      />
    </div>
  );
}
